/**
 * PermissionsContext — Expense
 *
 * Fetches module-level permissions from the HRMS backend
 * (GET /api/permissions/my-role?app=expense) using the hrms_token stored
 * in localStorage. Allows Access Control configured in HRMS to drive
 * which Expense nav sections are shown to each role.
 *
 * Usage:
 *   const { can } = usePermissions();
 *   can('approvals')  → true/false
 *   can('analytics')  → true/false
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const PermissionsContext = createContext(null);

const HRMS_API = process.env.REACT_APP_HRMS_API_URL || "http://localhost:5000/api";

// Roles that use individual grants only — no role-based fallback.
// Superadmin, admin, and executive keep role-based permissions.
const INDIVIDUAL_ONLY_ROLES = new Set(["employee", "team_leader", "manager"]);

// Every Expense manager needs these two team-management views to perform the
// role. Individual denies above still take precedence, but a missing one-off
// grant must not hide standard manager functionality.
const MANAGER_STANDARD_MODULES = new Set(["manager_analytics", "expense_report"]);

// ── Fallback defaults (used when HRMS API is unreachable) ─────────────────
// employee / team_leader / manager: intentionally empty — access comes only
// from individual module grants (Access Control → Individual Access → Grant).
const FALLBACK_DEFAULTS = {
  employee:    {}, // Individual grants only — no role-based defaults
  team_leader: {}, // Individual grants only — no role-based defaults
  manager:     {}, // Individual grants only — no role-based defaults
  admin:       null,  // null = grant all
  superadmin:  null,
  executive:   { dashboard:1, manager_analytics:1, admin_reports:1, audit_log:1 },
};

const DEFAULT_SCOPE = {
  employee: "self", team_leader: "team", manager: "team",
  admin: "company", superadmin: "all", executive: "all",
};

export function PermissionsProvider({ user, children }) {
  const [perms, setPerms]               = useState(null);
  const [empOverrides, setEmpOverrides] = useState({});
  const [loaded, setLoaded]             = useState(false);

  const loadPerms = useCallback(async () => {
    if (!user) { setPerms(null); setEmpOverrides({}); setLoaded(true); return; }

    const hrmsToken = localStorage.getItem("hrms_token");
    if (!hrmsToken) { setPerms(null); setEmpOverrides({}); setLoaded(true); return; }

    const effectiveRole = user?.app_role_overrides?.expense || user?.role;
    try {
      const res = await fetch(`${HRMS_API}/permissions/my-role?app=expense`, {
        headers: { Authorization: `Bearer ${hrmsToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows = data.permissions || [];
      const map  = {};
      for (const r of rows) {
        map[r.module_key] = {
          can_view:   !!r.can_view,
          can_create: !!r.can_create,
          can_edit:   !!r.can_edit,
          can_delete: !!r.can_delete,
          data_scope: r.data_scope || DEFAULT_SCOPE[effectiveRole] || "self",
          is_enabled: r.is_enabled !== false,
        };
      }
      setPerms(map);
      setEmpOverrides(data.employee_overrides || {});
    } catch {
      setPerms(null);
      setEmpOverrides({});
    } finally {
      setLoaded(true);
    }
  }, [user?.id, user?.role, user?.app_role_overrides?.expense]);

  useEffect(() => { loadPerms(); }, [loadPerms]);

  // ── can(moduleKey, action) ────────────────────────────────────────────────
  // Resolution order:
  //   1. Individual Expense override (grant → true, deny → false)
  //   2. employee / team_leader / manager → hard-stop false (individual grants only)
  //   3. superadmin / admin → full access
  //   4. executive / others → role-based DB permissions / fallback
  const can = useCallback((moduleKey, action = "view") => {
    if (!user) return false;
    const effectiveRole = user?.app_role_overrides?.expense || user?.role;

    // ── Step 1: individual Expense override (highest priority for all roles) ──
    const expOv = empOverrides?.expense || {};
    if (expOv[moduleKey] === "deny")  return false;
    if (expOv[moduleKey] === "grant") return true;

    if (effectiveRole === "manager" && MANAGER_STANDARD_MODULES.has(moduleKey)) {
      return true;
    }

    // ── Step 2: employee / team_leader / manager — individual grants only ──
    // No 'grant' override above → module is hidden. No role-based fallback.
    if (INDIVIDUAL_ONLY_ROLES.has(effectiveRole)) return false;

    // ── Step 3: superadmin + admin — full access ──
    if (effectiveRole === "superadmin" || effectiveRole === "admin") {
      if (!perms) return true;
      const m = perms[moduleKey];
      if (!m) return true;
      if (!m.is_enabled) return false;
      return m[`can_${action}`] !== false;
    }

    // ── Step 4: executive / others — role-based permissions ──
    if (!perms) {
      const fallback = FALLBACK_DEFAULTS[effectiveRole];
      if (fallback === null) return true;
      if (!fallback)         return false;
      return !!fallback[moduleKey];
    }

    const m = perms[moduleKey];
    if (!m) return false;
    if (!m.is_enabled) return false;
    return !!m[`can_${action}`];
  }, [user, perms, empOverrides]);

  const reloadPermissions = useCallback(() => {
    setLoaded(false);
    loadPerms();
  }, [loadPerms]);

  return (
    <PermissionsContext.Provider value={{ can, perms, empOverrides, loaded, reloadPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) return {
    can: () => true,
    perms: null,
    loaded: true,
    reloadPermissions: () => {},
  };
  return ctx;
}
